import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, Shield, User, Trash2, Edit2, Check, X, Plus, Building2, Crown, Key, Lock, Unlock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useBusiness } from '../context/BusinessContext';
import { useTheme } from '../context/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminUsers = ({ currentUser }) => {
  const { businesses } = useBusiness();
  const { isLight } = useTheme();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteConfirmUser, setDeleteConfirmUser] = useState(null);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    display_name: '',
    email: '',
    phone: '',
    role: 'user',
    business_id: ''
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!newUser.username.trim() || !newUser.password.trim() || !newUser.display_name.trim()) {
      toast.error('Username, password, and display name are required');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/users`, newUser, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User created successfully');
      setCreateOpen(false);
      setNewUser({
        username: '',
        password: '',
        display_name: '',
        email: '',
        phone: '',
        role: 'user',
        business_id: ''
      });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleUpdateUser = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const updateData = {
        role: editingUser.role,
        business_id: editingUser.business_id,
        display_name: editingUser.display_name
      };
      // Include password if it was changed
      if (editingUser.newPassword && editingUser.newPassword.trim()) {
        updateData.password = editingUser.newPassword;
      }
      await axios.put(`${API}/admin/users/${userId}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(editingUser.newPassword ? 'User updated with new password' : 'User updated');
      fetchUsers();
      setEditingUser(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword.trim() || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/users/${resetPasswordUser.id}`, {
        password: newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Password reset for ${resetPasswordUser.display_name || resetPasswordUser.username}`);
      setResetPasswordUser(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User deleted');
      fetchUsers();
      setDeleteConfirmUser(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleToggleLock = async (user) => {
    try {
      const token = localStorage.getItem('token');
      const newLockState = !user.is_locked;
      await axios.put(`${API}/admin/users/${user.id}`, {
        is_locked: newLockState
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(newLockState ? `${user.display_name || user.username} has been locked` : `${user.display_name || user.username} has been unlocked`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'super_admin':
        return <Crown className="w-5 h-5 text-amber-400" />;
      case 'admin':
        return <Shield className="w-5 h-5 text-violet-400" />;
      default:
        return <User className="w-5 h-5 text-slate-400" />;
    }
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'super_admin':
        return 'bg-amber-600/20 text-amber-400 border-amber-600/30';
      case 'admin':
        return 'bg-violet-600/20 text-violet-400 border-violet-600/30';
      default:
        return 'bg-slate-700 text-slate-300 border-slate-600';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className={isLight ? 'text-gray-500' : 'text-slate-400'}>Loading...</div>
      </div>
    );
  }

  return (
    <div className={`p-8 min-h-screen ${isLight ? 'bg-[#faf8f5]' : ''}`} data-testid="admin-users-page">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-3xl font-bold mb-2 flex items-center gap-3 ${isLight ? 'text-gray-800' : 'text-white'}`}>
              <Shield className={`w-8 h-8 ${isLight ? 'text-[#e94560]' : 'text-violet-400'}`} />
              User Management
            </h1>
            <p className={isLight ? 'text-gray-500' : 'text-slate-400'}>Create and manage user accounts across businesses</p>
          </div>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className={`rounded-full px-6 ${isLight ? 'bg-[#e94560] hover:bg-[#d63d56]' : 'bg-violet-600 hover:bg-violet-500'} text-white`} data-testid="create-user-button">
                <Plus className="w-4 h-4 mr-2" />
                Create User
              </Button>
            </DialogTrigger>
            <DialogContent className={isLight ? 'bg-white border-[#ebe7e0] text-gray-800' : 'bg-slate-900 border-slate-800 text-white'}>
              <DialogHeader>
                <DialogTitle className={isLight ? 'text-gray-800' : ''}>Create New User</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateUser} className="space-y-4">
                <div>
                  <Label className={isLight ? 'text-gray-600' : ''}>Username *</Label>
                  <Input
                    value={newUser.username}
                    onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                    placeholder="john_doe"
                    className={`mt-1.5 ${isLight ? 'bg-[#faf8f5] border-[#ebe7e0] text-gray-800' : 'bg-slate-950 border-slate-800 text-white'}`}
                    required
                  />
                </div>
                <div>
                  <Label className={isLight ? 'text-gray-600' : ''}>Password *</Label>
                  <Input
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    placeholder="••••••••"
                    className={`mt-1.5 ${isLight ? 'bg-[#faf8f5] border-[#ebe7e0] text-gray-800' : 'bg-slate-950 border-slate-800 text-white'}`}
                    required
                  />
                </div>
                <div>
                  <Label className={isLight ? 'text-gray-600' : ''}>Display Name *</Label>
                  <Input
                    value={newUser.display_name}
                    onChange={(e) => setNewUser({ ...newUser, display_name: e.target.value })}
                    placeholder="John Doe"
                    className={`mt-1.5 ${isLight ? 'bg-[#faf8f5] border-[#ebe7e0] text-gray-800' : 'bg-slate-950 border-slate-800 text-white'}`}
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className={isLight ? 'text-gray-600' : ''}>Email</Label>
                    <Input
                      type="email"
                      value={newUser.email}
                      onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                      placeholder="john@example.com"
                      className={`mt-1.5 ${isLight ? 'bg-[#faf8f5] border-[#ebe7e0] text-gray-800' : 'bg-slate-950 border-slate-800 text-white'}`}
                    />
                  </div>
                  <div>
                    <Label className={isLight ? 'text-gray-600' : ''}>Phone</Label>
                    <Input
                      value={newUser.phone}
                      onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                      placeholder="+1 234 567 8900"
                      className={`mt-1.5 ${isLight ? 'bg-[#faf8f5] border-[#ebe7e0] text-gray-800' : 'bg-slate-950 border-slate-800 text-white'}`}
                    />
                  </div>
                </div>
                <div>
                  <Label className={isLight ? 'text-gray-600' : ''}>Role</Label>
                  <Select value={newUser.role} onValueChange={(value) => setNewUser({ ...newUser, role: value })}>
                    <SelectTrigger className={`mt-1.5 ${isLight ? 'bg-[#faf8f5] border-[#ebe7e0] text-gray-800' : 'bg-slate-950 border-slate-800 text-white'}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={isLight ? 'bg-white border-[#ebe7e0] text-gray-800' : 'bg-slate-900 border-slate-800 text-white'}>
                      <SelectItem value="user" className={isLight ? 'hover:bg-gray-100' : ''}>User</SelectItem>
                      <SelectItem value="admin" className={isLight ? 'hover:bg-gray-100' : ''}>Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className={isLight ? 'text-gray-600' : ''}>Assign to Business</Label>
                  <Select value={newUser.business_id} onValueChange={(value) => setNewUser({ ...newUser, business_id: value })}>
                    <SelectTrigger className={`mt-1.5 ${isLight ? 'bg-[#faf8f5] border-[#ebe7e0] text-gray-800' : 'bg-slate-950 border-slate-800 text-white'}`}>
                      <SelectValue placeholder="Select a business" />
                    </SelectTrigger>
                    <SelectContent className={isLight ? 'bg-white border-[#ebe7e0] text-gray-800' : 'bg-slate-900 border-slate-800 text-white'}>
                      {businesses.map((business) => (
                        <SelectItem key={business.id} value={business.id} className={isLight ? 'hover:bg-gray-100' : ''}>
                          {business.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setCreateOpen(false)} className={`flex-1 ${isLight ? 'border-[#ebe7e0] text-gray-600 hover:bg-gray-50' : 'border-slate-700'}`}>
                    Cancel
                  </Button>
                  <Button type="submit" className={`flex-1 ${isLight ? 'bg-[#e94560] hover:bg-[#d63d56]' : 'bg-violet-600 hover:bg-violet-500'}`}>
                    Create User
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className={`rounded-xl border p-4 ${isLight ? 'border-[#ebe7e0] bg-white' : 'border-slate-800 bg-slate-900/40'}`}>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isLight ? 'bg-[#c4a35a]/10' : 'bg-amber-600/20'}`}>
                <Crown className={`w-5 h-5 ${isLight ? 'text-[#c4a35a]' : 'text-amber-400'}`} />
              </div>
              <div>
                <div className={`text-2xl font-bold ${isLight ? 'text-gray-800' : 'text-white'}`}>
                  {users.filter(u => u.role === 'super_admin').length}
                </div>
                <div className={`text-sm ${isLight ? 'text-gray-400' : 'text-slate-400'}`}>Super Admins</div>
              </div>
            </div>
          </div>
          <div className={`rounded-xl border p-4 ${isLight ? 'border-[#ebe7e0] bg-white' : 'border-slate-800 bg-slate-900/40'}`}>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isLight ? 'bg-[#e94560]/10' : 'bg-violet-600/20'}`}>
                <Shield className={`w-5 h-5 ${isLight ? 'text-[#e94560]' : 'text-violet-400'}`} />
              </div>
              <div>
                <div className={`text-2xl font-bold ${isLight ? 'text-gray-800' : 'text-white'}`}>
                  {users.filter(u => u.role === 'admin').length}
                </div>
                <div className={`text-sm ${isLight ? 'text-gray-400' : 'text-slate-400'}`}>Admins</div>
              </div>
            </div>
          </div>
          <div className={`rounded-xl border p-4 ${isLight ? 'border-[#ebe7e0] bg-white' : 'border-slate-800 bg-slate-900/40'}`}>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isLight ? 'bg-[#8b9a7d]/10' : 'bg-emerald-600/20'}`}>
                <User className={`w-5 h-5 ${isLight ? 'text-[#8b9a7d]' : 'text-emerald-400'}`} />
              </div>
              <div>
                <div className={`text-2xl font-bold ${isLight ? 'text-gray-800' : 'text-white'}`}>
                  {users.filter(u => u.role === 'user').length}
                </div>
                <div className={`text-sm ${isLight ? 'text-gray-400' : 'text-slate-400'}`}>Users</div>
              </div>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className={`rounded-xl border overflow-hidden ${isLight ? 'border-[#ebe7e0] bg-white' : 'border-slate-800 bg-slate-900/40'}`}>
          <div className={`p-4 border-b ${isLight ? 'border-[#ebe7e0]' : 'border-slate-800'}`}>
            <h2 className={`text-lg font-semibold flex items-center gap-2 ${isLight ? 'text-gray-800' : 'text-white'}`}>
              <Users className={`w-5 h-5 ${isLight ? 'text-gray-400' : 'text-slate-400'}`} />
              All Users ({users.length})
            </h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={isLight ? 'bg-gray-50' : 'bg-slate-800/50'}>
                <tr>
                  <th className={`text-left px-6 py-3 text-xs font-medium uppercase tracking-wider ${isLight ? 'text-gray-500' : 'text-slate-400'}`}>User</th>
                  <th className={`text-left px-6 py-3 text-xs font-medium uppercase tracking-wider ${isLight ? 'text-gray-500' : 'text-slate-400'}`}>Business</th>
                  <th className={`text-left px-6 py-3 text-xs font-medium uppercase tracking-wider ${isLight ? 'text-gray-500' : 'text-slate-400'}`}>Role</th>
                  <th className={`text-left px-6 py-3 text-xs font-medium uppercase tracking-wider ${isLight ? 'text-gray-500' : 'text-slate-400'}`}>Joined</th>
                  <th className={`text-right px-6 py-3 text-xs font-medium uppercase tracking-wider ${isLight ? 'text-gray-500' : 'text-slate-400'}`}>Actions</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isLight ? 'divide-[#ebe7e0]' : 'divide-slate-800'}`}>
                {users.map((user) => {
                  const isCurrentUser = user.id === currentUser.id;
                  const isEditing = editingUser?.id === user.id;
                  const isSuperAdmin = user.role === 'super_admin';
                  
                  return (
                    <tr key={user.id} className={`${isLight ? 'hover:bg-gray-50' : 'hover:bg-slate-800/30'} ${isCurrentUser ? (isLight ? 'bg-[#e94560]/5' : 'bg-violet-900/10') : ''}`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            user.role === 'super_admin' ? (isLight ? 'bg-[#c4a35a]/10' : 'bg-amber-600/20') :
                            user.role === 'admin' ? (isLight ? 'bg-[#e94560]/10' : 'bg-violet-600/20') : (isLight ? 'bg-gray-100' : 'bg-slate-700')
                          }`}>
                            {getRoleIcon(user.role)}
                          </div>
                          <div>
                            <div className={`font-medium ${isLight ? 'text-gray-800' : 'text-white'}`}>
                              {user.display_name || user.username}
                              {isCurrentUser && (
                                <span className={`ml-2 text-xs ${isLight ? 'text-[#e94560]' : 'text-violet-400'}`}>(You)</span>
                              )}
                              {user.is_locked && (
                                <span className="ml-2 text-xs text-red-500 font-semibold">(Locked)</span>
                              )}
                            </div>
                            <div className={`text-xs ${isLight ? 'text-gray-400' : 'text-slate-500'}`}>@{user.username}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {isEditing && !isSuperAdmin ? (
                          <Select
                            value={editingUser.business_id || ''}
                            onValueChange={(value) => setEditingUser({ ...editingUser, business_id: value })}
                          >
                            <SelectTrigger className={`w-40 h-8 ${isLight ? 'bg-white border-[#ebe7e0] text-gray-800' : 'bg-slate-900 border-slate-700 text-white'}`}>
                              <SelectValue placeholder="Select..." />
                            </SelectTrigger>
                            <SelectContent className={isLight ? 'bg-white border-[#ebe7e0] text-gray-800' : 'bg-slate-900 border-slate-700 text-white'}>
                              {businesses.map((business) => (
                                <SelectItem key={business.id} value={business.id}>
                                  {business.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <div className="flex items-center gap-2">
                            <Building2 className={`w-4 h-4 ${isLight ? 'text-gray-400' : 'text-slate-500'}`} />
                            <span className={`text-sm ${isLight ? 'text-gray-600' : 'text-slate-300'}`}>
                              {user.business_name || (isSuperAdmin ? 'All Businesses' : 'Not Assigned')}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {isEditing && !isSuperAdmin ? (
                          <div className="flex items-center gap-2">
                            <Select
                              value={editingUser.role}
                              onValueChange={(value) => setEditingUser({ ...editingUser, role: value })}
                            >
                              <SelectTrigger className={`w-28 h-8 ${isLight ? 'bg-white border-[#ebe7e0] text-gray-800' : 'bg-slate-900 border-slate-700 text-white'}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className={isLight ? 'bg-white border-[#ebe7e0] text-gray-800' : 'bg-slate-900 border-slate-700 text-white'}>
                                <SelectItem value="admin">Admin</SelectItem>
                                <SelectItem value="user">User</SelectItem>
                              </SelectContent>
                            </Select>
                            <Button
                              onClick={() => handleUpdateUser(user.id)}
                              className="h-8 w-8 p-0 bg-emerald-600 hover:bg-emerald-500"
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button
                              onClick={() => setEditingUser(null)}
                              variant="ghost"
                              className={`h-8 w-8 p-0 ${isLight ? 'hover:bg-gray-100' : 'hover:bg-slate-700'}`}
                            >
                              <X className={`w-4 h-4 ${isLight ? 'text-gray-400' : 'text-slate-400'}`} />
                            </Button>
                          </div>
                        ) : (
                          <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getRoleBadge(user.role)}`}>
                            {user.role === 'super_admin' ? 'Super Admin' : user.role === 'admin' ? 'Admin' : 'User'}
                          </span>
                        )}
                      </td>
                      <td className={`px-6 py-4 text-sm ${isLight ? 'text-gray-500' : 'text-slate-400'}`}>{formatDate(user.created_at)}</td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {!isEditing && (
                            <>
                              {!isCurrentUser && !isSuperAdmin && (
                                <Button
                                  onClick={() => handleToggleLock(user)}
                                  variant="ghost"
                                  className={`h-8 w-8 p-0 ${user.is_locked ? (isLight ? 'hover:bg-emerald-50' : 'hover:bg-emerald-900/30') : (isLight ? 'hover:bg-orange-50' : 'hover:bg-orange-900/30')}`}
                                  title={user.is_locked ? 'Unlock user' : 'Lock user'}
                                  data-testid={`toggle-lock-${user.id}`}
                                >
                                  {user.is_locked 
                                    ? <Lock className={`w-4 h-4 ${isLight ? 'text-red-500' : 'text-red-400'}`} />
                                    : <Unlock className={`w-4 h-4 ${isLight ? 'text-emerald-500' : 'text-emerald-400'}`} />
                                  }
                                </Button>
                              )}
                              <Button
                                onClick={() => setResetPasswordUser(user)}
                                variant="ghost"
                                className={`h-8 w-8 p-0 ${isLight ? 'hover:bg-[#c4a35a]/10' : 'hover:bg-amber-900/30'}`}
                                disabled={isCurrentUser || isSuperAdmin}
                                title={isCurrentUser ? "Cannot reset own password" : isSuperAdmin ? "Cannot reset super admin password" : "Reset password"}
                              >
                                <Key className={`w-4 h-4 ${isCurrentUser || isSuperAdmin ? (isLight ? 'text-gray-300' : 'text-slate-600') : (isLight ? 'text-[#c4a35a]' : 'text-amber-400')}`} />
                              </Button>
                              <Button
                                onClick={() => setEditingUser(user)}
                                variant="ghost"
                                className={`h-8 w-8 p-0 ${isLight ? 'hover:bg-gray-100' : 'hover:bg-slate-700'}`}
                                disabled={isCurrentUser || isSuperAdmin}
                                title={isCurrentUser ? "Cannot edit yourself" : isSuperAdmin ? "Cannot edit super admin" : "Edit user"}
                              >
                                <Edit2 className={`w-4 h-4 ${isCurrentUser || isSuperAdmin ? (isLight ? 'text-gray-300' : 'text-slate-600') : (isLight ? 'text-gray-400' : 'text-slate-400')}`} />
                              </Button>
                              <Button
                                onClick={() => setDeleteConfirmUser(user)}
                                variant="ghost"
                                className={`h-8 w-8 p-0 ${isLight ? 'hover:bg-red-50' : 'hover:bg-red-900/30'}`}
                                disabled={isCurrentUser || isSuperAdmin}
                                title={isCurrentUser ? "Cannot delete yourself" : isSuperAdmin ? "Cannot delete super admin" : "Delete user"}
                              >
                                <Trash2 className={`w-4 h-4 ${isCurrentUser || isSuperAdmin ? (isLight ? 'text-gray-300' : 'text-slate-600') : 'text-red-400'}`} />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Info Card */}
        <div className={`rounded-xl border p-4 ${isLight ? 'border-[#ebe7e0] bg-[#faf8f5]' : 'border-slate-800 bg-slate-800/30'}`}>
          <h3 className={`font-medium mb-2 ${isLight ? 'text-gray-800' : 'text-white'}`}>Role Permissions</h3>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <div className={`flex items-center gap-2 mb-1 ${isLight ? 'text-[#c4a35a]' : 'text-amber-400'}`}>
                <Crown className="w-4 h-4" />
                <span className="font-medium">Super Admin</span>
              </div>
              <ul className={`space-y-1 ml-6 ${isLight ? 'text-gray-500' : 'text-slate-400'}`}>
                <li>• Access all businesses</li>
                <li>• Create/manage businesses</li>
                <li>• Create/manage all users</li>
                <li>• Full system access</li>
              </ul>
            </div>
            <div>
              <div className={`flex items-center gap-2 mb-1 ${isLight ? 'text-[#e94560]' : 'text-violet-400'}`}>
                <Shield className="w-4 h-4" />
                <span className="font-medium">Admin</span>
              </div>
              <ul className={`space-y-1 ml-6 ${isLight ? 'text-gray-500' : 'text-slate-400'}`}>
                <li>• Access assigned business</li>
                <li>• Upload music/announcements</li>
                <li>• Create/schedule playlists</li>
                <li>• View dashboard</li>
              </ul>
            </div>
            <div>
              <div className={`flex items-center gap-2 mb-1 ${isLight ? 'text-[#8b9a7d]' : 'text-emerald-400'}`}>
                <User className="w-4 h-4" />
                <span className="font-medium">User</span>
              </div>
              <ul className={`space-y-1 ml-6 ${isLight ? 'text-gray-500' : 'text-slate-400'}`}>
                <li>• Dashboard view only</li>
                <li>• Listen to scheduled music</li>
                <li>• Mute/unmute audio</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirmUser} onOpenChange={() => setDeleteConfirmUser(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white">
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-slate-300">
              Are you sure you want to delete <span className="font-semibold text-white">{deleteConfirmUser?.display_name || deleteConfirmUser?.username}</span>?
            </p>
            <p className="text-sm text-slate-500">
              This action cannot be undone. All data associated with this user will be permanently deleted.
            </p>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setDeleteConfirmUser(null)}
                className="border-slate-700"
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleDeleteUser(deleteConfirmUser?.id)}
                className="bg-red-600 hover:bg-red-500"
              >
                Delete User
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={!!resetPasswordUser} onOpenChange={() => { setResetPasswordUser(null); setNewPassword(''); }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-400" />
              Reset Password
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-slate-300">
              Reset password for <span className="font-semibold text-white">{resetPasswordUser?.display_name || resetPasswordUser?.username}</span>
            </p>
            <div>
              <Label className="text-slate-400">New Password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 6 characters)"
                className="bg-slate-950 border-slate-800 text-white mt-1.5"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => { setResetPasswordUser(null); setNewPassword(''); }}
                className="border-slate-700"
              >
                Cancel
              </Button>
              <Button
                onClick={handleResetPassword}
                disabled={!newPassword.trim() || newPassword.length < 6}
                className="bg-amber-600 hover:bg-amber-500"
              >
                Reset Password
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsers;
